import { useState } from "react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Plus, X, Save, Calculator, Copy } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../../ui/command";
import { useAuth } from "../../../contexts/AuthContext";
import { useSaveAllCommissionAssignments } from "../../../lib/supabase/commission-model-assignments/supabaseCommissionModelAssignmentService";
import { toast } from "react-hot-toast";
import { useExchangeRates } from "../../../lib/supabase/exchange-rates/supabaseExchangeRateService";
import { useCreateCost, useRemoveExistingCostsByCategory } from "../../../lib/supabase/costs/supabaseCostService";
import { DateTime } from "luxon";
import { Alert, AlertDescription } from "../../ui/alert";
import { AlertTriangle } from "lucide-react";
import { calculateSplitAmounts } from "../miscpaymentsview/splitUtils";

interface CostAssignment {
  id: string;
  name: string;
  assigned_models: Array<{
    model_id: string;
    monthly_amounts: Record<string, number>; // Key format: "YYYY-MM"
  }>;
}

interface UserAssignmentsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assignments: CostAssignment[];
  onAssignmentsChange: (assignments: CostAssignment[]) => void;
  models: any[];
  selected_month: string; // Format: "YYYY-MM"
}

export const UserAssignmentsDialog = ({ open, onOpenChange, assignments, onAssignmentsChange, models, selected_month: selected_month }: UserAssignmentsDialogProps) => {
  const [newAssignmentName, setNewAssignmentName] = useState('');
  const [modelPopoverOpen, setModelPopoverOpen] = useState<Record<string, boolean>>({});
  const { user } = useAuth();
  const saveAssignments = useSaveAllCommissionAssignments();
  const { data: exchangeRates } = useExchangeRates();
  const createCost = useCreateCost();

  const removeExistingCosts = useRemoveExistingCostsByCategory();

  // Parse selectedMonth to get year and month
  const [year, month] = selected_month.split('-').map(Number);
  const monthDate = DateTime.fromObject({ year, month, day: 1 });

  const addAssignment = () => {
    if (newAssignmentName.trim()) {
      const newAssignment: CostAssignment = {
        id: crypto.randomUUID(),
        name: newAssignmentName.trim(),
        assigned_models: []
      };
      onAssignmentsChange([...assignments, newAssignment]);
      setNewAssignmentName('');
    }
  };

  const removeAssignment = (id: string) => {
    onAssignmentsChange(assignments.filter(a => a.id !== id));
  };

  const addModelToAssignment = (assignmentId: string, model_id: string) => {
    onAssignmentsChange(assignments.map(a =>
      a.id === assignmentId
        ? {
          ...a,
          assigned_models: [...a.assigned_models, {
            model_id,
            monthly_amounts: { [selected_month]: 0 }
          }]
        }
        : a
    ));
  };

  const removeModelFromAssignment = (assignmentId: string, model_id: string) => {
    onAssignmentsChange(assignments.map(a =>
      a.id === assignmentId
        ? { ...a, assigned_models: a.assigned_models.filter(m => m.model_id !== model_id) }
        : a
    ));
  };

  const updateModelAmount = (assignmentId: string, model_id: string, amount: number) => {
    onAssignmentsChange(assignments.map(a =>
      a.id === assignmentId
        ? {
          ...a,
          assigned_models: a.assigned_models.map(am =>
            am.model_id === model_id
              ? {
                ...am,
                monthly_amounts: {
                  ...am.monthly_amounts,
                  [selected_month]: amount
                }
              }
              : am
          )
        }
        : a
    ));
  };

  const getAvailableModels = () => {
    const assignedModelIds = new Set<string>();
    assignments.forEach(a => {
      a.assigned_models.forEach(assignment => assignedModelIds.add(assignment.model_id));
    });
    return models.filter(model => !assignedModelIds.has(model.id));
  };

  const handleSave = async () => {
    if (!user?.id) {
      toast.error('You must be logged in to save assignments');
      return;
    }

    try {
      // Transform assignments to the format expected by the service
      const assignmentsToSave = assignments.map(assignment => ({
        id: assignment.id,
        name: assignment.name,
        assigned_models: assignment.assigned_models.map(am => ({
          model_id: am.model_id,
          monthly_amounts: am.monthly_amounts
        }))
      }));

      await saveAssignments.mutateAsync({ assignments: assignmentsToSave, user_id: user.id });
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving assignments:', error);
      toast.error('Failed to save assignments');
    }
  };

  const getCurrentMonthAmount = (assignment: CostAssignment) => {
    return assignment.assigned_models.reduce((sum, am) => {
      return sum + (am.monthly_amounts[selected_month] || 0);
    }, 0);
  };

  const calculateCommission = async () => {
    if (!user?.id) {
      toast.error('You must be logged in to calculate commission');
      return;
    }

    // Check if exchange rate is available for the selected month
    const exchangeRate = exchangeRates?.find(rate => {
      const rateDate = rate.date;
      return rateDate.year === year && rateDate.month === month && rateDate.day === 1;
    });

    if (!exchangeRate) {
      toast.error(`Exchange rate not available for ${month}-${year}`);
      return;
    }

    try {
      // First, remove any existing commission costs for this month
      try {
        toast.loading('Removing existing commission costs...');
        const removedCount = await removeExistingCosts.mutateAsync({
          category: 'Commission',
          month: monthDate,
          metadata_filter: {
            key: 'notes',
            value: `Commission calculation for ${selected_month}`
          }
        });
        toast.dismiss();
        if (removedCount > 0) {
          toast.success(`Removed ${removedCount} existing commission costs`);
        }
      } catch (removeError) {
        toast.dismiss();
        console.error('Error: Could not remove existing costs, proceeding with calculation:', removeError);
        throw removeError;
      }

      // Calculate total monthly earnings (USD) from assignments
      let total_monthly_earnings_usd = 0;

      assignments.forEach(assignment => {
        const assignmentTotal = getCurrentMonthAmount(assignment);
        console.log(`Assignment ${assignment.name}: $${assignmentTotal.toFixed(2)}`);
        total_monthly_earnings_usd += assignmentTotal;
      });

      console.log(`Total Monthly Earnings USD: $${total_monthly_earnings_usd.toFixed(2)}`);

      if (total_monthly_earnings_usd === 0) {
        toast.error('No assignment amounts found for the selected month');
        return;
      }

      // Calculate 5% commission (USD) - full cost
      const fullCommissionUSD = total_monthly_earnings_usd * 0.05;

      // Cap commission at $100,000
      const commissionUSD = Math.min(fullCommissionUSD, 100000);

      // Convert to GBP using exchange rate
      const commissionGBP = commissionUSD * exchangeRate.rate;

      // Show calculation progress
      toast.loading('Creating commission costs...');

      // Create commission entries for each user with assignment
      const commissionPromises = assignments.map(async (assignment) => {
        const assignmentTotal = getCurrentMonthAmount(assignment);

        if (assignmentTotal > 0) {
          // Calculate 5% commission for this assignment (USD)
          const assignmentCommissionUSD = assignmentTotal * 0.05;

          // Cap individual assignment commission at $100,000
          const cappedCommissionUSD = Math.min(assignmentCommissionUSD, 100000);

          // Convert to GBP using exchange rate
          const assignmentCommissionGBP = cappedCommissionUSD * exchangeRate.rate;

          // Get all models in this assignment and sort by startDate
          const assignedModelIds = assignment.assigned_models.map(am => am.model_id);
          const assigned_models = models.filter(model => assignedModelIds.includes(model.id));

          // Sort all models by startDate (earliest first) to determine tipping point
          const sortedModels = assigned_models.sort((a, b) => a.created_a.toMillis() - b.created_at.toMillis());

          // Find the tipping point model (which model causes commission to exceed $100,000)
          let cumulativeCommission = 0;
          let tippingModel: any = null;
          let modelsBeforeTipping: any[] = [];

          for (const model of sortedModels) {
            const modelAmount = assignment.assigned_models.find(am => am.model_id === model.id)?.monthly_amounts[selected_month] || 0;
            const modelCommission = modelAmount * 0.05;

            if (cumulativeCommission + modelCommission > 100000 && !tippingModel) {
              tippingModel = model;
              break;
            }

            cumulativeCommission += modelCommission;
            modelsBeforeTipping.push(model);
          }

          // If no tipping point found, all models are included
          if (!tippingModel) {
            modelsBeforeTipping = sortedModels;
          }

          // Now split based on models BEFORE the tipping point
          const set1Models = modelsBeforeTipping.filter(model => model.set === 1);
          const set2Models = modelsBeforeTipping.filter(model => model.set === 2);

          const baseMetadata = {
            notes: `Commission calculation for ${selected_month}. Assignment: ${assignment.name}, Assignment USD: $${assignmentTotal.toFixed(2)}, Commission USD: $${assignmentCommissionUSD.toFixed(2)}, Capped Commission USD: $${cappedCommissionUSD.toFixed(2)}, Exchange Rate: ${exchangeRate.rate}, Commission GBP: £${assignmentCommissionGBP.toFixed(2)}. Tipping model: ${tippingModel ? tippingModel.name : 'None'}, Models before tipping: ${modelsBeforeTipping.length}/${sortedModels.length}`
          };

          if (set1Models.length > 0 && set2Models.length > 0) {
            // Both sets - create split entries based on model count proportion from models BEFORE tipping point
            const split_id = crypto.randomUUID();
            const totalModels = set1Models.length + set2Models.length;
            const set1Percentage = (set1Models.length / totalModels) * 100;

            const { set1Amount, set2Amount } = calculateSplitAmounts(assignmentCommissionGBP, 0, set1Percentage);

            // Create both split entries
            await Promise.all([
              createCost.mutateAsync({
                cost: {
                  name: `${assignment.name} - Commission`,
                  set: 1,
                  amount: set1Amount,
                  category: 'Commission',
                  metadata: { ...baseMetadata, split_id: split_id }
                },
                month: monthDate
              }),
              createCost.mutateAsync({
                cost: {
                  name: `${assignment.name} - Commission`,
                  set: 2,
                  amount: set2Amount,
                  category: 'Commission',
                  metadata: { ...baseMetadata, split_id: split_id }
                },
                month: monthDate
              })
            ]);
          } else {
            // Single set or no set info
            const costSet = set2Models.length > 0 ? 2 : 1;
            const costName = set1Models.length > 0 || set2Models.length > 0
              ? `${assignment.name} - Commission (Set ${costSet})`
              : `${assignment.name} - Commission`;

            await createCost.mutateAsync({
              cost: {
                name: costName,
                set: costSet,
                amount: assignmentCommissionGBP,
                category: 'Commission',
                metadata: baseMetadata
              },
              month: monthDate
            });
          }
        }
      });

      await Promise.all(commissionPromises);
      toast.dismiss();

      const successMessage = fullCommissionUSD > 100000
        ? `Commission calculated and created successfully! Full: £${(fullCommissionUSD * exchangeRate.rate).toFixed(2)}, Capped: £${commissionGBP.toFixed(2)}`
        : `Commission calculated and created successfully! Total: £${commissionGBP.toFixed(2)}`;
      toast.success(successMessage);
    } catch (error) {
      toast.dismiss();
      console.error('Error calculating commission:', error);
      toast.error('Failed to calculate commission');
    }
  };

  const getExchangeRateForMonth = () => {
    if (!exchangeRates) return null;

    // Check for exchange rate for the selected month (1st of current month)
    return exchangeRates.find(rate => {
      const rateDate = rate.date;
      return rateDate.year === year && rateDate.month === month && rateDate.day === 1;
    });
  };

  const isCommissionButtonEnabled = () => {
    const exchangeRate = getExchangeRateForMonth();

    // Check if there are assignments with amounts
    const hasAssignmentsWithAmounts = assignments.some(assignment =>
      getCurrentMonthAmount(assignment) > 0
    );

    return !!exchangeRate && assignments.length > 0 && hasAssignmentsWithAmounts;
  };

  const calculateFinalCommissionForModel = (assignmentId: string, modelAmount: number) => {
    const exchangeRate = getExchangeRateForMonth();
    if (!exchangeRate || modelAmount === 0) return 0;

    // Find the assignment this model belongs to
    const assignment = assignments.find(a => a.id === assignmentId);
    if (!assignment) return 0;

    // Calculate assignment total
    const assignmentTotal = getCurrentMonthAmount(assignment);
    if (assignmentTotal === 0) return 0;

    // Get the model to determine which set it belongs to
    const assignedModelIds = assignment.assigned_models.map(am => am.model_id);
    const assigned_models = models.filter(model => assignedModelIds.includes(model.id));
    const model = assigned_models.find(m => m.id === assignment.assigned_models.find(am => am.model_id === m.id)?.model_id);

    if (!model) return 0;

    // Sort all models by startDate to find tipping point
    const sortedModels = assigned_models.sort((a, b) => a.created_at.toMillis() - b.created_at.toMillis());

    // Find the tipping point model (which model causes commission to exceed $100,000)
    let cumulativeCommission = 0;
    let tippingModel: any = null;
    let modelsBeforeTipping: any[] = [];

    for (const m of sortedModels) {
      const mAmount = assignment.assigned_models.find(am => am.model_id === m.id)?.monthly_amounts[selected_month] || 0;
      const mCommission = mAmount * 0.05;

      if (cumulativeCommission + mCommission > 100000 && !tippingModel) {
        tippingModel = m;
        break;
      }

      cumulativeCommission += mCommission;
      modelsBeforeTipping.push(m);
    }

    // If no tipping point found, all models are included
    if (!tippingModel) {
      modelsBeforeTipping = sortedModels;
    }

    // Calculate this model's direct commission contribution (always show this)
    const modelCommissionUSD = modelAmount * 0.05;

    // Cap the model's commission if it would exceed the $100,000 limit
    const cappedModelCommissionUSD = Math.min(modelCommissionUSD, 100000);

    // Convert to GBP using exchange rate
    const modelCommissionGBP = cappedModelCommissionUSD * exchangeRate.rate;

    return modelCommissionGBP;
  };

  const calculateAssignmentCommission = (assignmentId: string) => {
    const exchangeRate = getExchangeRateForMonth();
    if (!exchangeRate) return 0;

    // Find the assignment
    const assignment = assignments.find(a => a.id === assignmentId);
    if (!assignment) return 0;

    // Calculate assignment total
    const assignmentTotal = getCurrentMonthAmount(assignment);
    if (assignmentTotal === 0) return 0;

    // Calculate 5% commission for this assignment (USD)
    const assignmentCommissionUSD = assignmentTotal * 0.05;

    // Cap individual assignment commission at $100,000
    const cappedCommissionUSD = Math.min(assignmentCommissionUSD, 100000);

    // Convert to GBP using exchange rate
    const assignmentCommissionGBP = cappedCommissionUSD * exchangeRate.rate;

    // Determine which sets the models in this assignment belong to
    const assignedModelIds = assignment.assigned_models.map(am => am.model_id);
    const assigned_models = models.filter(model => assignedModelIds.includes(model.id));
    const set1Models = assigned_models.filter(model => model.set === 1);
    const set2Models = assigned_models.filter(model => model.set === 2);

    if (set1Models.length > 0 && set2Models.length > 0) {
      // Both sets exist - return the total commission (will be split between sets)
      return assignmentCommissionGBP;
    } else {
      // Single set or no set info - return the full assignment commission
      return assignmentCommissionGBP;
    }
  };

  // Helper function to determine if a model is included in commission calculation
  const isModelIncludedInCommission = (assignmentId: string, model_id: string) => {
    const assignment = assignments.find(a => a.id === assignmentId);
    if (!assignment) return false;

    const assignedModelIds = assignment.assigned_models.map(am => am.model_id);
    const assigned_models = models.filter(model => assignedModelIds.includes(model.id));

    // Sort all models by startDate to find tipping point
    const sortedModels = assigned_models.sort((a, b) => a.created_at.toMillis() - b.created_at.toMillis());

    // Find the tipping point model
    let cumulativeCommission = 0;
    let tippingModel: any = null;
    let modelsBeforeTipping: any[] = [];

    for (const model of sortedModels) {
      const modelAmount = assignment.assigned_models.find(am => am.model_id === model.id)?.monthly_amounts[selected_month] || 0;
      const modelCommission = modelAmount * 0.05;

      if (cumulativeCommission + modelCommission > 100000 && !tippingModel) {
        tippingModel = model;
        break;
      }

      cumulativeCommission += modelCommission;
      modelsBeforeTipping.push(model);
    }

    // If no tipping point found, all models are included
    if (!tippingModel) {
      modelsBeforeTipping = sortedModels;
    }

    // Check if this model is included
    return modelsBeforeTipping.some(m => m.id === model_id);
  };

  // Helper function to get the tipping model for an assignment
  const getTippingModel = (assignmentId: string) => {
    const assignment = assignments.find(a => a.id === assignmentId);
    if (!assignment) return null;

    const assignedModelIds = assignment.assigned_models.map(am => am.model_id);
    const assigned_models = models.filter(model => assignedModelIds.includes(model.id));

    // Sort all models by startDate to find tipping point
    const sortedModels = assigned_models.sort((a, b) => a.created_at.toMillis() - b.created_at.toMillis());

    // Find the tipping point model
    let cumulativeCommission = 0;
    let tippingModel: any = null;

    for (const model of sortedModels) {
      const modelAmount = assignment.assigned_models.find(am => am.model_id === model.id)?.monthly_amounts[selected_month] || 0;
      const modelCommission = modelAmount * 0.05;

      if (cumulativeCommission + modelCommission > 100000 && !tippingModel) {
        tippingModel = model;
        break;
      }

      cumulativeCommission += modelCommission;
    }

    return tippingModel;
  };

  // Helper function to calculate how much a tipping model contributed vs didn't contribute
  const getTippingModelCommissionBreakdown = (assignmentId: string, model_id: string) => {
    const assignment = assignments.find(a => a.id === assignmentId);
    if (!assignment) return { contributed: 0, notContributed: 0 };

    const assignedModelIds = assignment.assigned_models.map(am => am.model_id);
    const assigned_models = models.filter(model => assignedModelIds.includes(model.id));

    // Sort all models by startDate to find tipping point
    const sortedModels = assigned_models.sort((a, b) => a.created_at.toMillis() - b.created_at.toMillis());

    // Find the tipping point model
    let cumulativeCommission = 0;
    let tippingModel: any = null;

    for (const model of sortedModels) {
      const modelAmount = assignment.assigned_models.find(am => am.model_id === model.id)?.monthly_amounts[selected_month] || 0;
      const modelCommission = modelAmount * 0.05;

      if (cumulativeCommission + modelCommission > 100000 && !tippingModel) {
        tippingModel = model;
        break;
      }

      cumulativeCommission += modelCommission;
    }

    if (!tippingModel || tippingModel.id !== model_id) {
      return { contributed: 0, notContributed: 0 };
    }

    // Calculate how much this model contributed and how much it didn't
    const modelAmount = assignment.assigned_models.find(am => am.model_id === model_id)?.monthly_amounts[selected_month] || 0;
    const modelCommission = modelAmount * 0.05;
    const contributed = 100000 - cumulativeCommission; // Amount that fits under the cap
    const notContributed = modelCommission - contributed; // Amount that exceeds the cap

    return { contributed, notContributed };
  };

  const copyToClipboard = async () => {
    try {
      let clipboardText = '';

      assignments.forEach(assignment => {
        clipboardText += `${assignment.name}\n`;

        const assigned_models = models.filter(m => assignment.assigned_models.some(am => am.model_id === m.id))
          .sort((a, b) => a.created_at.toMillis() - b.created_at.toMillis());

        assigned_models.forEach(model => {
          const modelAssignment = assignment.assigned_models.find(am => am.model_id === model.id);
          const earnings = modelAssignment?.monthly_amounts[selected_month] || 0;
          const startDate = model.created_at.toFormat('dd/MM/yyyy');

          clipboardText += `${model.name},${startDate},${earnings}\n`;
        });

        clipboardText += '\n';
      });

      await navigator.clipboard.writeText(clipboardText.trim());
      toast.success('Assignment data copied to clipboard!');
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      toast.error('Failed to copy to clipboard');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden" aria-describedby="commission-user-assignments">
        <DialogHeader>
          <DialogTitle>Commission User Assignments</DialogTitle>
        </DialogHeader>

        {/* Exchange Rate Warning Banner */}
        {!getExchangeRateForMonth() && (
          <Alert className="h-12 border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950">
            <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            <AlertDescription className="text-amber-800 dark:text-amber-200">
              Exchange rate not set for {year}-{month.toString().padStart(2, '0')}.
              Commission calculation requires the selected month exchange rate.
            </AlertDescription>
          </Alert>
        )}

        <div className="flex flex-col h-[calc(90vh-220px)]">
          {/* Add new user */}
          <div className="flex items-center gap-2 pb-4 border-b">
            <Input
              placeholder="Enter assignment name..."
              value={newAssignmentName}
              onChange={(e) => setNewAssignmentName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addAssignment()}
              className="flex-1"
            />
            <Button onClick={addAssignment} disabled={!newAssignmentName.trim()}>
              <Plus className="h-4 w-4" />
            </Button>
          </div>

          {/* Assignments grid with scrollable content */}
          <div className="flex-1 overflow-y-auto py-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
              {assignments.map(assignment => {
                const assigned_models = models.filter(m => assignment.assigned_models.some(am => am.model_id === m.id))
                  .sort((a, b) => a.created_at.toMillis() - b.created_a.toMillis());
                const availableModels = getAvailableModels();
                const currentMonthAmount = getCurrentMonthAmount(assignment);

                return (
                  <Card key={assignment.id} className="shadow-sm h-fit">
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-sm font-medium">{assignment.name}</CardTitle>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">
                            {assigned_models.length} models
                          </span>
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-brand-green">
                              ${currentMonthAmount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                            </span>
                            {getExchangeRateForMonth() && currentMonthAmount > 0 && (
                              <span className="text-xs font-medium text-brand-gold">
                                £{(() => {
                                  const commission = calculateAssignmentCommission(assignment.id);
                                  return commission > 0 ? commission.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",") : "N/A";
                                })()}
                              </span>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeAssignment(assignment.id)}
                            className="h-6 w-6 p-0 text-brand-red hover:text-brand-red"
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {/* Scrollable models list */}
                      <div className="max-h-64 overflow-y-auto">
                        {assigned_models.length > 0 ? (
                          <div className="space-y-2">
                            {assigned_models.map(model => {
                              const modelAssignment = assignment.assigned_models.find(am => am.model_id === model.id);
                              const currentAmount = modelAssignment?.monthly_amounts[selected_month] || 0;
                              return (
                                <div key={model.id} className={`rounded p-2 ${getTippingModel(assignment.id)?.id === model.id
                                    ? 'bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800'
                                    : isModelIncludedInCommission(assignment.id, model.id)
                                      ? 'bg-brand-dark dark:bg-brand-dark'
                                      : 'bg-brand-red dark:bg-brand-dark border border-brand-red dark:border-brand-border'
                                  }`}>
                                  <div className="flex items-center justify-between mb-1">
                                    <div className="flex items-center gap-2 min-w-0 flex-1">
                                      <span className={`text-xs font-medium truncate ${getTippingModel(assignment.id)?.id === model.id
                                          ? 'text-amber-800 dark:text-amber-200'
                                          : isModelIncludedInCommission(assignment.id, model.id)
                                            ? ''
                                            : 'text-brand-red dark:text-brand-red'
                                        }`}>
                                        {model.name}
                                      </span>
                                      <span className="text-xs text-muted-foreground flex-shrink-0">
                                        {model.created_at.toFormat('dd/MM/yyyy')}
                                      </span>
                                      {getTippingModel(assignment.id)?.id === model.id && (
                                        <span className="text-xs text-amber-700 dark:text-amber-300 font-medium">
                                          (Tipping Point)
                                        </span>
                                      )}
                                      {!isModelIncludedInCommission(assignment.id, model.id) && getTippingModel(assignment.id)?.id !== model.id && (
                                        <span className="text-xs text-brand-red dark:text-brand-red font-medium">
                                          (Excluded - After Cap)
                                        </span>
                                      )}
                                    </div>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => removeModelFromAssignment(assignment.id, model.id)}
                                      className="h-4 w-4 p-0 text-brand-red hover:text-brand-red flex-shrink-0"
                                    >
                                      <X className="h-2.5 w-2.5" />
                                    </Button>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {/* USD Input */}
                                    <div className="flex items-center gap-1 flex-1">
                                      <span className="text-xs text-muted-foreground">$</span>
                                      <Input
                                        type="number"
                                        step="1"
                                        min="0"
                                        value={currentAmount === 0 ? '' : currentAmount}
                                        onChange={(e) => {
                                          const newAmount = parseFloat(e.target.value) || 0;
                                          updateModelAmount(assignment.id, model.id, newAmount);
                                        }}
                                        className="h-6 text-xs"
                                        placeholder="0.00"
                                      />
                                    </div>

                                    {/* Commission Display */}
                                    <div className="flex items-center gap-1 flex-1">
                                      <span className="text-xs text-muted-foreground">£</span>
                                      <div className={`h-6 px-2 text-xs rounded border flex items-center justify-center min-w-[60px] ${getTippingModel(assignment.id)?.id === model.id
                                          ? 'bg-amber-100 dark:bg-amber-900 border-amber-300 dark:border-amber-700'
                                          : isModelIncludedInCommission(assignment.id, model.id)
                                            ? 'bg-muted'
                                            : 'bg-brand-red dark:bg-brand-dark border-brand-red dark:border-brand-red'
                                        }`}>
                                        {(() => {
                                          if (getTippingModel(assignment.id)?.id === model.id) {
                                            // Show breakdown for tipping model
                                            const breakdown = getTippingModelCommissionBreakdown(assignment.id, model.id);
                                            const exchangeRate = getExchangeRateForMonth();
                                            if (!exchangeRate) return "N/A";

                                            const contributedGBP = breakdown.contributed * exchangeRate.rate;
                                            const notContributedGBP = breakdown.notContributed * exchangeRate.rate;

                                            return `${contributedGBP.toFixed(2)}/${notContributedGBP.toFixed(2)}`;
                                          }

                                          const commission = calculateFinalCommissionForModel(assignment.id, currentAmount);
                                          if (commission === 0) return "N/A";
                                          const formattedCommission = commission.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");

                                          // Show commission amount but indicate if excluded
                                          if (!isModelIncludedInCommission(assignment.id, model.id)) {
                                            return `${formattedCommission}*`;
                                          }

                                          return formattedCommission;
                                        })()}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="text-center py-4 text-muted-foreground">
                            <span className="text-xs">No models assigned</span>
                          </div>
                        )}
                      </div>

                      {/* Add model button - outside scroll */}
                      {availableModels.length > 0 && (
                        <Popover
                          open={modelPopoverOpen[assignment.id]}
                          onOpenChange={(open) => setModelPopoverOpen(prev => ({ ...prev, [assignment.id]: open }))}
                        >
                          <PopoverTrigger asChild>
                            <Button variant="outline" size="sm" className="w-full text-xs">
                              <Plus className="h-3 w-3 mr-1" />
                              Add Model
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-[300px] p-0" align="start">
                            <Command>
                              <CommandInput placeholder="Search models..." />
                              <CommandList className="max-h-60">
                                <CommandEmpty>No models available.</CommandEmpty>
                                <CommandGroup>
                                  {availableModels.map((model) => (
                                    <CommandItem
                                      key={model.id}
                                      onSelect={() => {
                                        addModelToAssignment(assignment.id, model.id);
                                        setModelPopoverOpen(prev => ({ ...prev, [assignment.id]: false }));
                                      }}
                                      className="text-sm"
                                    >
                                      {model.name}
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Legend for excluded models */}
          <div className="pt-2 border-t">
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-brand-dark dark:bg-brand-dark border border-brand-border dark:border-brand-border rounded"></div>
                <span>Included in commission</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded"></div>
                <span>Tipping point model (contributed/not contributed)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-brand-red dark:bg-brand-dark border border-brand-red dark:border-brand-border rounded"></div>
                <span>Excluded from commission (after cap)</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-brand-red dark:text-brand-red font-medium">*</span>
                <span>Commission amount if included</span>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter className="flex flex-col sm:flex-row gap-2">
          <Button
            onClick={copyToClipboard}
            disabled={assignments.length === 0}
            variant="outline"
            className="w-full sm:w-auto"
          >
            <Copy className="h-4 w-4" />
          </Button>
          <Button
            onClick={calculateCommission}
            disabled={!isCommissionButtonEnabled() || createCost.isPending || removeExistingCosts.isPending}
            variant="outline"
            className="w-full sm:w-auto"
          >
            <Calculator className="h-4 w-4 mr-2" />
            {createCost.isPending || removeExistingCosts.isPending ? 'Calculating...' : 'Calculate Commission'}
          </Button>
          <Button
            onClick={handleSave}
            disabled={saveAssignments.isPending}
            className="w-full sm:w-auto"
          >
            <Save className="h-4 w-4 mr-2" />
            {saveAssignments.isPending ? 'Saving...' : 'Save Assignments'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};